import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Bookmark, CalendarPlus, Navigation, MapPin, Trash2, Loader2, Route, Link2, Car } from "lucide-react";
import { api } from "@/api/client";

// Frontend heuristic — map the native `category` to an on-brand tactical badge.
// (Zero backend/schema change; category is read straight from the saved store.)
const priorityBadge = (cat) => {
  const c = (cat || "").toLowerCase();
  if (["services", "finance"].includes(c)) return "LOGISTICS";
  if (["accommodation", "lodging"].includes(c)) return "SAFESTAY";
  if (["dining", "food"].includes(c)) return "SUPPLY";
  return "CHECKPOINT";
};

// Strict payload whitelist — only these keys ever reach add-stop; anything else
// is dropped before the request leaves the client.
const ALLOWED_STOP_KEYS = ["name", "address", "category", "place_id", "latitude", "longitude"];
const cleanStop = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([k]) => ALLOWED_STOP_KEYS.includes(k)));

const directionsUrl = (lat, lng, name, address) =>
  lat != null && lng != null
    ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${name || ""} ${address || ""}`)}`;

const Badge = ({ label }) => (
  <span className="font-mono text-[7px] font-black uppercase tracking-wider border border-black bg-charcoal px-1.5 py-0.5 text-gold">
    {label}
  </span>
);

// Client-side Haversine — straight-line km between two coord pairs (no server hit).
const haversineKm = (lat1, lng1, lat2, lng2) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Heuristic ETA at a baseline 60 km/h → "X hr Y min".
const etaLabel = (km) => {
  const mins = Math.round((km / 60) * 60);
  if (mins < 1) return "0 min";
  const h = Math.floor(mins / 60), m = mins % 60;
  return h ? `${h} hr ${m} min` : `${m} min`;
};

// Dual distance/ETA badge. Fail-soft: no origin or missing/invalid coords → "-- km".
const DistanceBadge = ({ origin, lat, lng }) => {
  const la = parseFloat(lat), lo = parseFloat(lng);
  const ready = origin && Number.isFinite(la) && Number.isFinite(lo);
  const km = ready ? haversineKm(origin.lat, origin.lng, la, lo) : null;
  return (
    <span data-testid="dist-badge"
      className="flex shrink-0 items-center gap-1 border-2 border-black bg-[#D4AF37] px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-tight text-black">
      <MapPin size={10} strokeWidth={2.8} />
      {km == null ? "-- km" : `${km.toFixed(1)} km`}
      {km != null && <>·<Car size={10} strokeWidth={2.8} />{etaLabel(km)}</>}
    </span>
  );
};

export default function RelocationBoard() {
  const [saved, setSaved] = useState(null);
  const [stops, setStops] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  // Live device position (client-only, never sent to backend). Fail-soft to null.
  const [origin, setOrigin] = useState(null);
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => setOrigin({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => setOrigin(null),
      { timeout: 6000, maximumAge: 300000 }
    );
  }, []);

  const loadSaved = useCallback(
    () => api.get("/local/saved").then((r) => setSaved(r.data.places || [])).catch(() => setSaved([])),
    []
  );
  const loadStops = useCallback(
    () => api.get("/relocation/itinerary").then((r) => setStops(r.data.stops || [])).catch(() => setStops([])),
    []
  );

  useEffect(() => { loadSaved(); loadStops(); }, [loadSaved, loadStops]);

  const addToTrip = async (p) => {
    setBusy(true);
    try {
      await api.post("/relocation/itinerary/add-stop", cleanStop({
        name: p.name, address: p.address || "", category: p.category || "",
        place_id: p.place_id, latitude: p.latitude, longitude: p.longitude,
      }));
      toast.success("Added to The Escape Plan");
      await loadStops();
    } catch { toast.error("Couldn't add that stop"); }
    finally { setBusy(false); }
  };

  const removeHaven = async (placeId) => {
    setSaved((prev) => (prev || []).filter((p) => p.place_id !== placeId));
    try { await api.delete(`/local/saved/${encodeURIComponent(placeId)}`); }
    catch { toast.error("Couldn't remove"); loadSaved(); }
  };

  const removeStop = async (id) => {
    setStops((prev) => (prev || []).filter((s) => s.id !== id));
    try { await api.delete(`/relocation/itinerary/stop/${encodeURIComponent(id)}`); }
    catch { toast.error("Couldn't remove stop"); loadStops(); }
  };

  // Drag-to-reorder — swap locally for zero latency, renumber `order`, then
  // persist the whole sequence. On failure, revert to the pre-drag order.
  const reorderStops = async (from, to) => {
    if (from == null || from === to || !stops) return;
    const prev = stops;
    const next = [...stops];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    const renumbered = next.map((s, idx) => ({ ...s, order: idx }));
    setStops(renumbered);
    try {
      await api.patch("/relocation/itinerary/reorder",
        renumbered.map((s) => ({ stop_id: s.id, order: s.order })));
    } catch { toast.error("Couldn't save the new order"); setStops(prev); }
  };

  // Shareable board link — shares the ROUTING PATH only (never auth state); the
  // recipient loads their own tenant-scoped board once authenticated.
  const shareBoard = async () => {
    const url = `${window.location.origin}/?tab=command&hub=lifestyle_hub&sub=board`;
    let ok = false;
    try { if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(url); ok = true; } } catch { /* fallthrough */ }
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        ok = document.execCommand("copy"); document.body.removeChild(ta);
      } catch { /* noop */ }
    }
    if (ok) toast.success("Board Access Link Copied to Clipboard");
    else toast(url, { description: "Copy this board link" });
  };

  const Empty = ({ text }) => (
    <p className="px-1 py-6 text-center font-mono text-[10px] leading-relaxed text-charcoal/50" data-testid="board-empty">
      {text}
    </p>
  );

  return (
    <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pt-3 pb-28" data-testid="relocation-board">
      <div className="mb-3 flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/60">Relocation Board</p>
          <h2 className="font-serif text-xl text-charcoal">Havens & Escape Plan</h2>
        </div>
        <button onClick={shareBoard} data-testid="board-copy-link"
          className="flex shrink-0 items-center gap-1 border-2 border-black bg-gold px-2.5 py-1.5 font-mono text-[9px] font-black uppercase tracking-widest text-black shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
          <Link2 size={13} strokeWidth={2.6} /> Copy Link
        </button>
      </div>

      {/* ── Card A: My Saved Havens ─────────────────────────────── */}
      <section className="mb-4 border-2 border-black bg-[#FAF8F5] p-3 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]" data-testid="saved-havens">
        <div className="mb-2 flex items-center gap-2">
          <Bookmark size={14} strokeWidth={2.6} className="text-charcoal" />
          <p className="font-mono text-[10px] font-black uppercase tracking-widest text-charcoal">My Saved Havens</p>
          <span className="ml-auto font-mono text-[9px] font-black text-charcoal/50">{saved?.length ?? 0}</span>
        </div>
        {saved === null ? (
          <div className="grid place-items-center py-6"><Loader2 className="animate-spin text-charcoal/60" size={18} /></div>
        ) : saved.length === 0 ? (
          <Empty text="No safe-havens mapped yet. Explore Local Discovery to secure your first zone." />
        ) : (
          <div className="space-y-2">
            {saved.map((p) => (
              <div key={p.place_id} data-testid={`haven-${p.place_id}`} className="border-2 border-black bg-white p-2.5">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className="truncate font-serif text-sm text-charcoal">{p.name}</p>
                      <Badge label={priorityBadge(p.category)} />
                    </div>
                    {p.address && (
                      <p className="mt-0.5 flex items-start gap-1 font-mono text-[9px] text-charcoal/60">
                        <MapPin size={10} className="mt-0.5 shrink-0" /> <span className="truncate">{p.address}</span>
                      </p>
                    )}
                  </div>
                  <button onClick={() => removeHaven(p.place_id)} data-testid={`haven-remove-${p.place_id}`}
                    className="shrink-0 grid h-7 w-7 place-items-center border-2 border-black bg-white text-charcoal/60 active:translate-y-[1px]">
                    <Trash2 size={12} />
                  </button>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <DistanceBadge origin={origin} lat={p.latitude} lng={p.longitude} />
                  <button onClick={() => addToTrip(p)} disabled={busy} data-testid={`haven-addtrip-${p.place_id}`}
                    className="flex items-center gap-1 border-2 border-black bg-gold px-2.5 py-1.5 font-mono text-[9px] font-black uppercase tracking-widest text-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-60">
                    <CalendarPlus size={12} /> Add to Trip
                  </button>
                  <a href={directionsUrl(p.latitude, p.longitude, p.name, p.address)} target="_blank" rel="noopener noreferrer"
                    data-testid={`haven-directions-${p.place_id}`}
                    className="ml-auto flex items-center gap-1 border-2 border-black bg-charcoal px-2.5 py-1.5 font-mono text-[9px] font-black uppercase tracking-widest text-gold shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none">
                    <Navigation size={12} /> Directions
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Card B: The Escape Plan ─────────────────────────────── */}
      <section className="border-2 border-black bg-charcoal p-3 shadow-[4px_4px_0px_0px_rgba(212,175,55,1)]" data-testid="escape-plan">
        <div className="mb-2 flex items-center gap-2">
          <Route size={14} strokeWidth={2.6} className="text-gold" />
          <p className="font-mono text-[10px] font-black uppercase tracking-widest text-gold">The Escape Plan</p>
          <span className="ml-auto font-mono text-[9px] font-black text-white/50">{stops?.length ?? 0} stops</span>
        </div>
        {stops === null ? (
          <div className="grid place-items-center py-6"><Loader2 className="animate-spin text-gold/70" size={18} /></div>
        ) : stops.length === 0 ? (
          <p className="px-1 py-6 text-center font-mono text-[10px] leading-relaxed text-white/50" data-testid="plan-empty">
            No checkpoints mapped yet. Add a saved haven to start your route.
          </p>
        ) : (
          <div className="space-y-2">
            {stops.map((s, i) => (
              <div key={s.id} data-testid={`stop-${s.id}`}
                draggable
                onDragStart={() => setDragIndex(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); reorderStops(dragIndex, i); setDragIndex(null); }}
                onDragEnd={() => setDragIndex(null)}
                className={`flex items-center gap-2.5 border-2 border-black p-2.5 cursor-grab active:cursor-grabbing ${dragIndex === i ? "bg-[#D4AF37] border-dashed text-black opacity-80" : "bg-[#FAF8F5]"}`}>
                <span className="grid h-7 w-7 shrink-0 place-items-center border-2 border-black bg-gold font-mono text-[11px] font-black text-black">
                  {(s.order ?? i) + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="truncate font-serif text-sm text-charcoal">{s.name}</p>
                    <Badge label={priorityBadge(s.category)} />
                  </div>
                  {s.address && <p className="truncate font-mono text-[9px] text-charcoal/60">{s.address}</p>}
                </div>
                <DistanceBadge origin={origin} lat={s.lat} lng={s.lng} />
                <a href={directionsUrl(s.lat, s.lng, s.name, s.address)} target="_blank" rel="noopener noreferrer"
                  data-testid={`stop-directions-${s.id}`}
                  className="shrink-0 grid h-7 w-7 place-items-center border-2 border-black bg-charcoal text-gold active:translate-y-[1px]">
                  <Navigation size={12} />
                </a>
                <button onClick={() => removeStop(s.id)} data-testid={`stop-remove-${s.id}`}
                  className="shrink-0 grid h-7 w-7 place-items-center border-2 border-black bg-white text-charcoal/60 active:translate-y-[1px]">
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
